import React from 'react';
import { Rect, Ellipse } from 'react-konva';
import Konva from 'konva';
import WithCursorPointer from 'components/canvas/components/with-pointer-cursor';
import useNodeDimensionsOperations from 'components/canvas/hooks/use-node-dimensions-operations';
import { useGlobalLinks } from 'state/canvas/node-linking/global-links';
import {
  useIsLinkArrowShown,
  useNodeLinkingOperations,
} from 'state/canvas/node-linking';
import { useSelectedNodeOperations } from 'state/canvas/selected-node';
import {
  SurfaceNodePointsToPosition,
  SurfaceNodeType,
  SurfaceNodeUnion,
} from 'graphql/types';
import { isMobile } from 'react-device-detect';
import { Position } from 'types';
import useTranslateWindowPointToCanvas from 'components/canvas/hooks/use-translate-window-point-to-canvas';
import useFetchSurfaceNodesFromCache from 'components/canvas/hooks/use-fetch-surface-nodes-from-cache';
import useCanvas from 'components/canvas/hooks/use-canvas';

const { Left, Bottom, Right, Top } = SurfaceNodePointsToPosition;

function LinkPoints(): any {
  const isLinkArrowShown = useIsLinkArrowShown();

  const [[globalLink], { removeGlobalLink }] = useGlobalLinks();

  const fetchSurfaceNodesFromCache = useFetchSurfaceNodesFromCache();

  const { layerInstance } = useCanvas();

  const {
    getIsLinkArrowShown,
    clearLink,
    removeLinkArrow,
    deleteLink,
    linkNodes,
    getLinkMarkedForDeletion,
    getCurrentLink,
    addConnection,
    removeConnection,
  } = useNodeLinkingOperations();

  const { getNodeDimensions } = useNodeDimensionsOperations();

  const { setSelectedNode } = useSelectedNodeOperations();

  async function linkTwoNodes(link) {
    // optimistic remove and add new connection
    const linkMarkedForDeletion = getLinkMarkedForDeletion();

    if (linkMarkedForDeletion?.to?.id) {
      removeConnection(linkMarkedForDeletion);
    }

    addConnection(link);

    clearLink();

    setSelectedNode(null);

    if (getIsLinkArrowShown()) {
      removeLinkArrow();
    }

    if (linkMarkedForDeletion?.to?.id) {
      await deleteLink(linkMarkedForDeletion);
    }

    if (globalLink && globalLink.from.id === link.from.id) {
      removeGlobalLink(globalLink);
    }

    const response = await linkNodes(link);

    if (response.isFailure) {
      //removeConnection(link);
    }
  }

  function onRectClick(position, node: SurfaceNodeUnion) {
    return function (event) {
      event.cancelBubble = true;
      const link = getCurrentLink();

      if (link.from?.id) {
        const tryingToLinkingWithSelf = link?.from?.id === node.id;

        if (tryingToLinkingWithSelf) {
          clearLink();
          return removeLinkArrow();
        }

        linkTwoNodes({ from: link.from, to: { ...node, position } });
      }
    };
  }

  const fill = '#0078D3';

  const translatePointToCanvas = useTranslateWindowPointToCanvas();

  function filterBackgroundNodes(node: SurfaceNodeUnion) {
    const stagePosition = translatePointToCanvas({ x: 0, y: 0 });

    return node.__typename !== 'SurfaceBackgroundNode';
  }

  function filterNodesInViewport(node: SurfaceNodeUnion) {
    const stagePosition = translatePointToCanvas({ x: 0, y: 0 });

    const position = node.position;

    if (stagePosition && position) {
      const stageStartX = stagePosition?.x;
      const stageEndX = stagePosition?.x + window.innerWidth * 2;
      const stageStartY = stagePosition?.y;
      const stageEndY = stagePosition?.y + window.innerHeight * 2;

      const isOnX = position?.x >= stageStartX && position?.x <= stageEndX;
      const isOnY = position?.y >= stageStartY && position?.y <= stageEndY;

      return isOnX && isOnY;
    }

    return false;
  }

  return React.useMemo(() => {
    if (isLinkArrowShown) {
      return (
        fetchSurfaceNodesFromCache()
          .filter(filterBackgroundNodes)
          // .filter(filterNodesInViewport)
          .map((node) => {
            const { width: nodeWidth, height: nodeHeight } = getNodeDimensions(
              node,
            );

            const position = node.position as Position;

            if (node.type === SurfaceNodeType.SubSurface) {
              return (
                <WithCursorPointer key={node.id}>
                  <Ellipse
                    /*offsetX={-145}
                    offsetY={-85}*/
                    x={position.x + 145}
                    y={position.y + 85}
                    width={300}
                    height={163}
                    radiusX={150}
                    radiusY={82}
                    fill={fill}
                    opacity={0.6}
                    {...{
                      [isMobile ? 'onTouchEnd' : 'onClick']: onRectClick(
                        Top,
                        node,
                      ),
                    }}
                  />
                </WithCursorPointer>
              );
            }

            return (
              <WithCursorPointer key={node.id}>
                <Rect
                  x={position.x}
                  y={position.y}
                  width={nodeWidth}
                  height={nodeHeight}
                  fill={fill}
                  {...(node.type === SurfaceNodeType.Note
                    ? { cornerRadius: [0, 27, 16, 0] }
                    : { cornerRadius: [0, 27, 0, 0] })}
                  opacity={0.6}
                  {...{
                    [isMobile ? 'onTouchEnd' : 'onClick']: onRectClick(
                      Top,
                      node,
                    ),
                  }}
                />
              </WithCursorPointer>
            );
          })
      );
    }

    return null;
  }, [isLinkArrowShown]);
}

export default LinkPoints;
